import React, { useContext, useMemo } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import * as styles from './sitemap.module.css';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import { PageMetadataContext } from '../../templates/PageMetaDataContext';
import { prefixWithLang } from '../../global/countries';

const SitemapComp: React.FC = React.memo(() => {
    let languages = [
        'default',
        'gb-en',
        'my-en',
        'vn-en',
        'de-de',
        'fr-fr',
        'sg-en',
    ];
    const countryMap = {
        default: 'United States',
        'au-en': 'Australia',
        'nz-en': 'New Zealand',
        'gb-en': 'United Kingdom',
        'my-en': 'Malaysia',
        'vn-en': 'Vietnam',
        'de-de': 'Germany',
        'fr-fr': 'France',
        'sg-en': 'Singapore',
    };
    const pageMetadata = useContext(PageMetadataContext);
    const preferredLanguage = pageMetadata.preferredLanguage?.toLowerCase();
    if (
        preferredLanguage !== 'au-en' &&
        preferredLanguage !== 'nz-en' &&
        languages.includes(preferredLanguage)
    ) {
        languages = [
            preferredLanguage,
            ...languages.filter((l) => l !== preferredLanguage),
        ];
    }

    const breakpoints = useBreakpoint();

    const queriedData = useStaticQuery(graphql`
        {
            allKontentItemLayoutMasterPage(
                filter: {
                    elements: {
                        exclude_from_internal_indexing: {
                            value: { elemMatch: { codename: { eq: "no" } } }
                        }
                    }
                }
            ) {
                nodes {
                    elements {
                        display_page_name {
                            value
                        }
                        slug {
                            value
                            url
                        }
                    }
                    preferred_language
                }
            }
            allSitePage(
                filter: {
                    pageContext: { ignoreInternalIndexing: { eq: "no" } }
                }
            ) {
                nodes {
                    path
                }
            }
        }
    `);

    const updatedPages = useMemo(() => {
        return queriedData.allKontentItemLayoutMasterPage.nodes
            .map((node) => {
                const display_page_name =
                    node.elements.display_page_name?.value || '';
                let url = node.elements.slug?.url || '';
                const lang = node.preferred_language?.toLowerCase() || '';

                if (!url || url === 'undefined') return null;

                url = prefixWithLang(
                    url,
                    lang === 'default' ? undefined : lang
                );

                if (!url.endsWith('/')) {
                    url += '/';
                }

                return { display_page_name, lang, url };
            })
            .filter(Boolean); // filters out null entries
    }, [queriedData]);

    const cleanedGroupedByLanguage = useMemo(() => {
        const langPrefixObject = Object.fromEntries(
            languages
                .filter((lang) => lang !== 'default')
                .map((lang) => [`/${lang}/`, lang])
        );
        const grouped: Record<
            string,
            { url: string; displayPageName: string }[]
        > = {};
        const seenFirstUrl: Record<string, boolean> = {};

        languages.forEach((lang) => {
            grouped[lang] = [];
        });

        queriedData.allSitePage.nodes.forEach((node) => {
            let path = node.path;
            if (!path.endsWith('/')) path += '/';

            if (
                (preferredLanguage === 'au-en' ||
                    preferredLanguage === 'nz-en') &&
                path.startsWith('/gb-en')
            ) {
                return;
            }

            const normalizedUrl = path.toLowerCase();
            let assignedLang = 'default';

            for (const prefix in langPrefixObject) {
                if (normalizedUrl.startsWith(prefix)) {
                    assignedLang = langPrefixObject[prefix];
                    break;
                }
            }

            if (!seenFirstUrl[assignedLang]) {
                seenFirstUrl[assignedLang] = true;
                return;
            }

            // Match page info from updatedPages
            let matched = updatedPages.find(
                (p) => p.lang === assignedLang && p.url === path
            );
            if (!matched) {
                const targetLastSegment = path.split('/').filter(Boolean).pop();
                matched = updatedPages.find((p) => {
                    const pageLastSegment = p.url
                        .split('/')
                        .filter(Boolean)
                        .pop();
                    return pageLastSegment === targetLastSegment;
                });
            }

            grouped[assignedLang].push({
                url: path,
                displayPageName: matched?.display_page_name || path,
            });
        });

        // Move blogs to the end within each group
        for (const lang in grouped) {
            const urls = grouped[lang];
            const blogs = urls.filter((entry) =>
                entry.url.toLowerCase().includes('/blog/')
            );
            const others = urls.filter(
                (entry) => !entry.url.toLowerCase().includes('/blog/')
            );
            grouped[lang] = [...others, ...blogs];
        }

        // Remove empty groups
        const cleaned = Object.fromEntries(
            Object.entries(grouped).filter(([_, pages]) => pages.length > 0)
        );

        return cleaned;
    }, [queriedData, preferredLanguage, languages, updatedPages]);

    const createPageTree = (pages) => {
        const root = [];
        const urlToNode = {};
        const validUrls = pages.map((p) => p.url);

        pages.forEach(({ url, displayPageName }) => {
            const parts = url.split('/').filter(Boolean);
            let currentPath = '';
            let parentPath = '/';
            let currentParent = root;

            parts.forEach((part, index) => {
                currentPath = parentPath + part + '/';
                const isLeaf = index === parts.length - 1;
                const shouldCreateNode =
                    isLeaf || validUrls.includes(currentPath);

                if (shouldCreateNode && !urlToNode[currentPath]) {
                    const node = {
                        url: currentPath,
                        displayPageName: isLeaf ? displayPageName : part,
                        children: [],
                    };
                    urlToNode[currentPath] = node;
                    currentParent.push(node);
                }

                if (urlToNode[currentPath]) {
                    currentParent = urlToNode[currentPath].children;
                }

                parentPath = currentPath;
            });
        });

        return root;
    };
    const pageTrees = useMemo(() => {
        const trees = {};
        Object.keys(cleanedGroupedByLanguage).forEach((lang) => {
            trees[lang] = createPageTree(cleanedGroupedByLanguage[lang]);
        });
        return trees;
    }, [cleanedGroupedByLanguage]);

    const renderTree = (nodes, isLastNodeBlog = false) => {
        if (isLastNodeBlog && breakpoints.md) {
            const allExceptLast = nodes.slice(0, -1);
            const lastNode = nodes[nodes.length - 1];
            return (
                <div className={styles.firstLevelWrapper}>
                    <div>
                        <ul className={styles.sitemapUl}>
                            {allExceptLast.map((node) => (
                                <li key={node.url}>
                                    <a href={node.url}>
                                        {node.displayPageName}
                                    </a>
                                    {node.children.length > 0 &&
                                        renderTree(node.children)}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <ul className={styles.sitemapUl}>
                            {lastNode && (
                                <li key={lastNode.url}>
                                    <a href={lastNode.url}>
                                        {lastNode.displayPageName}
                                    </a>
                                    {lastNode.children.length > 0 &&
                                        renderTree(lastNode.children)}
                                </li>
                            )}
                        </ul>
                    </div>
                </div>
            );
        }

        return (
            <ul>
                {nodes.map((node) => (
                    <li key={node.url}>
                        <a href={node.url}>{node.displayPageName}</a>
                        {node.children.length > 0 && renderTree(node.children)}
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <>
            {Object.keys(pageTrees).map((lang) => {
                const nodes = pageTrees[lang];
                const isLastNodeBlog =
                    nodes[nodes.length - 1]?.url.includes('/blog/');
                const countryName = countryMap[preferredLanguage];
                return (
                    <div
                        key={lang}
                        className={
                            !isLastNodeBlog ? styles.nonBlogspecfcStyles : ''
                        }
                    >
                        <a
                            className={`${styles.CountryName} ${
                                isLastNodeBlog
                                    ? styles.blogcountrynameStyles
                                    : ''
                            }`}
                            href={lang === 'default' ? '/' : `/${lang}`}
                        >
                            <h3
                                id={
                                    preferredLanguage != 'au-en' &&
                                    preferredLanguage != 'nz-en'
                                        ? countryMap[lang]
                                              ?.toLowerCase()
                                              ?.replace(/\s+/g, '')
                                        : countryName
                                              ?.toLowerCase()
                                              ?.replace(/\s+/g, '')
                                }
                            >
                                {preferredLanguage != 'au-en' &&
                                preferredLanguage != 'nz-en'
                                    ? countryMap[lang]
                                    : countryName}
                            </h3>
                        </a>
                        {renderTree(nodes, isLastNodeBlog)}
                    </div>
                );
            })}
        </>
    );
});

export default SitemapComp;
